/**
 * Bezier Path Strategy
 *
 * Path calculation using cubic bezier curves with edge-based anchors.
 * Creates smooth, curved connections between element EDGES (not centers).
 */

import type { Bound } from '@blocksuite/affine/global/gfx';

import type { AnchorPosition, EdgePathType, Point } from '../../types';
import { BasePathStrategy } from './base-path-strategy';
import type { IEdgePathStrategy } from './types';

/**
 * Cubic bezier path strategy with edge anchors.
 * Creates smooth curves connecting the nearest edge midpoints.
 */
export class BezierPathStrategy
  extends BasePathStrategy
  implements IEdgePathStrategy
{
  readonly type: EdgePathType = 'bezier';

  /** Control point offset factor (0-1). Higher = more curved. */
  private readonly curveFactor: number;

  constructor(curveFactor = 0.3) {
    super();
    this.curveFactor = curveFactor;
  }

  calculatePath(
    source: Bound,
    target: Bound,
    sourceAnchorHint?: AnchorPosition,
    targetAnchorHint?: AnchorPosition
  ): string {
    const { sourceAnchor: s, targetAnchor: t } = this.findAnchors(
      source,
      target,
      sourceAnchorHint,
      targetAnchorHint
    );

    const sourceSide = this.resolveAnchorSide(source, s, sourceAnchorHint);
    const targetSide = this.resolveAnchorSide(target, t, targetAnchorHint);

    const dist = this.distance(s, t);
    const offset = Math.max(dist * this.curveFactor, 30);

    const cp1 = this.controlPointOutward(s, sourceSide, offset);
    const cp2 = this.controlPointOutward(t, targetSide, offset);

    return `M ${s.x} ${s.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${t.x} ${t.y}`;
  }

  getPathPoints(
    source: Bound,
    target: Bound,
    segments = 20,
    sourceAnchorHint?: AnchorPosition,
    targetAnchorHint?: AnchorPosition
  ): Point[] {
    const { sourceAnchor: s, targetAnchor: t } = this.findAnchors(
      source,
      target,
      sourceAnchorHint,
      targetAnchorHint
    );

    const sourceSide = this.resolveAnchorSide(source, s, sourceAnchorHint);
    const targetSide = this.resolveAnchorSide(target, t, targetAnchorHint);

    const dist = this.distance(s, t);
    const offset = Math.max(dist * this.curveFactor, 30);

    const c1 = this.controlPointOutward(s, sourceSide, offset);
    const c2 = this.controlPointOutward(t, targetSide, offset);

    const points: Point[] = [];
    for (let i = 0; i <= segments; i++) {
      const u = i / segments;
      points.push(this.cubicBezierPoint(s, c1, c2, t, u));
    }

    return points;
  }

  calculateSelfLoopPath(bound: Bound): string {
    return this.buildSelfLoopPath(bound);
  }

  getSelfLoopPathPoints(bound: Bound, segments = 20): Point[] {
    return this.buildSelfLoopPathPoints(bound, segments);
  }
}
